import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import Card from 'app/frontend/base/components/Card'
import TeamApplyViewButtons from './TeamApplyViewButtons'
import { useAuth } from 'app/frontend/base/config/context/auth'
import { Calendar, Users } from 'lucide-react'
import GuildTeamRosterSummary from './GuildTeamRosterSummary'
import DefaultTeamStar from 'app/frontend/base/components/DefaultTeamStar'
import EventAvatar from 'app/frontend/base/components/EventAvatar'
import ItemLevelBadge from 'app/frontend/base/pages/TeamDashboard/subpages/TeamRoster/components/ItemLevelBadge'
import { difficultiesLabels } from 'app/frontend/types/custom/Difficulty'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { usePageInfo } from 'app/frontend/base/config/queries'
import dayjs from 'dayjs'
import { cn } from '@/lib/utils'

const GuildTeamCard = ({
  team,
  roster,
  eventDays = [],
  medianItemLevel,
  raidProgress,
}: {
  team: wowaudit.TeamReference
  roster?: wowaudit.TeamMembership[]
  eventDays?: string[]
  medianItemLevel?: number | null
  raidProgress?: NonNullable<wowaudit.OverviewData['raidProgress']>
}) => {
  const { t } = useTranslation()
  const { accessLevel } = useAuth()
  const {
    data: { currentSeason },
  } = usePageInfo()
  const dayLabels = eventDays.map((day) =>
    dayjs().day(Number(day)).format('ddd'),
  )
  const eventDaysLabel = dayLabels.join(', ') || '-'

  const title = (
    <h3 className='font-semibold text-lg flex items-center gap-1.5 min-w-0'>
      {team.default ? <DefaultTeamStar /> : null}
      <span className='truncate'>{team.name}</span>
    </h3>
  )

  return (
    <Card className='pt-3 px-4 pb-5 flex flex-col gap-3' key={team.id}>
      <div className='flex flex-wrap items-start justify-between gap-x-6 gap-y-3'>
        {accessLevel === 'none' ? (
          title
        ) : (
          <Link to={team.path} className='min-w-0'>
            {title}
          </Link>
        )}

        <div className='flex flex-wrap items-center justify-end gap-x-6 gap-y-2 whitespace-nowrap'>
          <div className='flex flex-wrap items-center gap-x-6 gap-y-2'>
            <span className='flex items-center gap-1.5 truncate'>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Calendar className='h-4 w-4 shrink-0' />
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  Weekly events are scheduled on these days
                </TooltipContent>
              </Tooltip>
              <span className='truncate'>{eventDaysLabel}</span>
            </span>
            {raidProgress ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className='flex items-center gap-1.5'>
                    <EventAvatar
                      instances={raidProgress.instances}
                      className='h-5 w-5 border'
                    />
                    <span>
                      {raidProgress.kills} / {raidProgress.total}{' '}
                      {difficultiesLabels[raidProgress.difficulty]}
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  {currentSeason.expansion} {currentSeason.name}
                </TooltipContent>
              </Tooltip>
            ) : null}
            {medianItemLevel ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <ItemLevelBadge
                      details={{ maxItemLevel: medianItemLevel }}
                      className='bg-transparent border-none hover:bg-transparent'
                      size='lg'
                    />
                  </span>
                </TooltipTrigger>
                <TooltipContent>Median team item level</TooltipContent>
              </Tooltip>
            ) : null}
            <span className='flex items-center gap-1.5'>
              <Users className='h-4 w-4' />
              <span>{team.characterAmount}</span>
            </span>
          </div>

          <div className='flex items-center gap-2'>
            <TeamApplyViewButtons team={team} />
          </div>
        </div>
      </div>

      <p
        className={cn(
          team.description ? 'whitespace-pre-wrap' : 'opacity-50 italic',
          'mb-4',
        )}
      >
        {team.description || t('shared.no-description')}
      </p>

      <GuildTeamRosterSummary members={roster} />
    </Card>
  )
}

export default GuildTeamCard
